import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import addressApi from "api/landlord/address";
import { useNavigate, useParams } from "react-router-dom";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  SortingState
} from "@tanstack/react-table";
import { MagicLink, Unit } from "types/landlord/address";
import { FC, useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { Switch } from "shared/components/forms/Switch";
import { unitStatus } from "shared/app/UnitsTableView/constants";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  Show
} from "shared/components";
import { Table } from "shared/app/Table";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { useSettingsController } from "views/settings-integration/controller";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { Door } from "providers/Layout/components/Door";
import { Database01 } from "untitledui-js-base";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types/partner";
import { RentAmount } from "../../../../components/RentAmount";
import { Property, PropertyKind } from "shared/types";
import { TemplateLinksSubmenu } from "shared/components/TemplateLinksSubmenu/TemplateLinksSubmenu";

type Props = {
  currentProperty?: Property;
  data: Unit[];
  handleCopyLink: (link: string) => void;
  setSelectedUnitId: (id: number) => void;
  setShowInviteApplicantsModal: (show: boolean) => void;
  setShowDeleteUnitModal: (show: boolean) => void;
  hasSearchParams?: boolean;
  hideProperty?: boolean;
  isLoading?: boolean;
  handleAvailableForApplication: (unit: Unit) => void;
  sorting?: SortingState;
  setSorting?: (sorting: SortingState) => void;
  sortableKeys?: string[];
};

const getEmptyStateText = ({
  currentProperty,
  hasSearchParams,
  unitsDictionary,
  propertyDictionary
}: {
  currentProperty?: Property;
  hasSearchParams?: boolean;
  unitsDictionary: string;
  propertyDictionary: string;
}) => {
  if (hasSearchParams) return "We couldn't find any data matching your query";

  if (currentProperty?.kind === PropertyKind.MultiFamily)
    return `Add ${unitsDictionary} to the mutil family ${propertyDictionary}`;

  return `Add ${unitsDictionary} to the ${propertyDictionary}`;
};

export const UnitsTable: FC<Props> = ({
  currentProperty,
  handleAvailableForApplication,
  data,
  handleCopyLink,
  setSelectedUnitId,
  setShowInviteApplicantsModal,
  setShowDeleteUnitModal,
  hasSearchParams,
  hideProperty,
  isLoading,
  sorting = [],
  setSorting,
  sortableKeys = []
}) => {
  const { partnerFeatures } = useAuthContext();
  const { propertyId } = useParams();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const {
    units,
    unit: unitDictionary,
    Unit,
    Property,
    property,
    Units
  } = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const { type, pms } = useAuthContext();
  const { isEverySyncEnabled } = useSettingsController();

  const columns = useMemo(() => {
    const cols: ColumnDef<any, any>[] = [
      {
        accessorKey: "title",
        header: Unit,
        id: "title",
        cell: (p) => (
          <p className="font-medium text-content-1000 max-w-[460px] truncate">
            {p.getValue()}
          </p>
        )
      }
    ];

    if (!hideProperty)
      cols.push({
        accessorFn: (row) => getShortAddressFromAddress(row.property.address),
        header: Property,
        cell: (p) => {
          const unit = p.row.original;
          return (
            <>
              <p className="overflow-hidden max-w-[460px] text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                {unit?.property?.name}
              </p>
              <p className="overflow-hidden max-w-[460px] text-ellipsis whitespace-nowrap text-xs text-content-400">
                {p.getValue()}
              </p>
            </>
          );
        }
      });

    cols.push({
      accessorKey: "property.property_group",
      header: "Property group",
      cell: (p) => (
        <p className="font-medium text-content-1000">
          {p.getValue()?.name || "--"}
        </p>
      )
    });

    cols.push({
      accessorKey: "occupied",
      header: "Current status",
      maxSize: 120,
      cell: (p) => {
        const row = p.row.original;
        const occupied = p.getValue();
        return (
          <div>
            <Badge
              size={BadgeSize.SM}
              variant={occupied ? BadgeVariant.PRIMARY : BadgeVariant.SUCCESS}
            >
              {unitStatus(occupied)}
            </Badge>
            {!row.pms_occupied_override && row.pms_occupied !== occupied ? (
              <p className="text-xs text-content-400">Manually overridden</p>
            ) : null}
          </div>
        );
      }
    });

    if (type.isPMS)
      cols.push({
        maxSize: 140,
        accessorKey: "pms_id",
        header: "Source",
        cell: (p) => {
          const unit = p.row.original;
          return (
            <p className="font-medium text-content-1000">
              {getSourceLabel(unit)}
            </p>
          );
        }
      });

    if (hasPropertyOwner)
      cols.push({
        accessorKey: "property.owner",
        header: "Owner",
        maxSize: 100,
        cell: (p) => (
          <p className="font-medium text-content-1000 truncate  max-w-[100px]">
            {p.getValue() || "--"}
          </p>
        )
      });

    if (pms.isManageAmerica) {
      cols.push({
        accessorKey: "home_rent_amount",
        header: "Home rent",
        cell: (p) => {
          const row = p.row.original;
          return (
            <>
              <RentAmount value={p.getValue()} />

              {!row.pms_home_rent_amount_override &&
              row.pms_home_rent_amount?.cents !== p.getValue()?.cents ? (
                <p className="text-xs text-content-400">Manually overridden</p>
              ) : null}
            </>
          );
        }
      });

      cols.push({
        accessorKey: "site_rent_amount",
        header: "Site rent",
        cell: (p) => {
          const row = p.row.original;
          return (
            <>
              <RentAmount value={p.getValue()} />

              {!row.pms_site_rent_amount_override &&
              row.pms_site_rent_amount?.cents !== p.getValue()?.cents ? (
                <p className="text-xs text-content-400">Manually overridden</p>
              ) : null}
            </>
          );
        }
      });
    }

    cols.push({
      accessorKey: "monthly_rent_amount",
      maxSize: 120,
      header: pms.isManageAmerica ? "Total rent" : "Rent amount",
      cell: (p) => {
        const row = p.row.original;
        return (
          <>
            <RentAmount value={p.getValue()} />

            {!row.pms_monthly_rent_amount_override &&
            row.pms_monthly_rent_amount?.cents !== p.getValue()?.cents ? (
              <p className="text-xs text-content-400">Manually overridden</p>
            ) : null}
          </>
        );
      }
    });

    if (type.isBoomScreen)
      cols.push(
        {
          accessorKey: "magic_link",
          header: "Allow applications",
          size: 120,
          cell: (p) => {
            const magicLinkData = p.row.original.magic_link as MagicLink | null;
            const active = magicLinkData?.active || false;
            const id = magicLinkData?.id || 0;
            return isEverySyncEnabled && p.row.original.pms_id ? (
              <AllowOrDisallowApplications
                isActive={p.row.original.magic_link?.active}
                withText={false}
              />
            ) : (
              <Switch
                checked={active}
                onChange={(active) => {
                  (async () => {
                    try {
                      const res = await addressApi.updateMagicLink(id, {
                        active,
                        listed:
                          p.row.original?.property?.kind === null
                            ? undefined
                            : p.row.original?.listed
                      });
                      handleAvailableForApplication(res.unit);
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    }
                  })();
                }}
              />
            );
          }
        },
        {
          id: "popover-action",
          header: "",
          size: 32,
          cell: (p) => {
            const unit = p.row.original;

            const actions = [
              {
                title: "Review",
                onClick: (e) => {
                  e.stopPropagation();
                  navigate(
                    `/properties/${unit?.property?.id}/units/${unit?.id}`
                  );
                }
              },
              {
                title: `Invite to ${unitDictionary}`,
                onClick: (e) => {
                  e.stopPropagation();
                  setSelectedUnitId(unit?.id);
                  setShowInviteApplicantsModal(true);
                }
              },
              {
                title: (
                  <TemplateLinksSubmenu
                    variant="COMPACT"
                    disabled={!p.row.original.magic_link?.active}
                    onCopyLink={(link) => {
                      setSelectedUnitId(unit?.id);
                      handleCopyLink(link);
                    }}
                    defaultLink={unit.magic_link?.application_link ?? ""}
                    unitId={unit?.id ? String(unit.id) : undefined}
                  />
                ),
                disabled: !p.row.original.magic_link?.active,
                tooltip: !p.row.original.magic_link?.active
                  ? `When a ${unitDictionary} is not accepting applications, applicants can only be invited via email`
                  : undefined,
                onClick: (e) => {
                  e.stopPropagation();
                  // Prevent default click behavior as submenu handles it
                }
              },
              {
                title: `Delete ${unitDictionary}`,
                onClick: (e) => {
                  e.stopPropagation();
                  if (unit.can_delete && !unit.pms_id) {
                    setShowDeleteUnitModal(true);
                    setSelectedUnitId(unit?.id);
                  }
                },
                color:
                  unit.can_delete && !unit.pms_id
                    ? "text-danger-500"
                    : "text-content-100",
                disabled: !unit.can_delete || unit.pms_id,
                tooltip: unit.pms_id
                  ? `You can't delete a PMS ${unitDictionary}`
                  : !unit?.can_delete
                    ? `You can't delete a ${unitDictionary} with applications`
                    : undefined
              }
            ];

            return <ContextMenuPopover actions={actions} />;
          }
        }
      );
    else
      cols.push({
        id: "popover-action",
        header: "",
        cell: (p) => {
          const unit = p.row.original;

          const actions = [
            {
              title: "Review",
              onClick: (e) => {
                e.stopPropagation();
                navigate(`/properties/${unit?.property?.id}/units/${unit?.id}`);
              }
            },
            {
              title: `Delete ${unitDictionary}`,
              onClick: (e) => {
                e.stopPropagation();
                setShowDeleteUnitModal(true);
                setSelectedUnitId(unit?.id);
              },
              color:
                unit.can_delete || !type.isPMS
                  ? "text-content-100"
                  : "text-danger-500",
              disabled: !unit.can_delete || !type.isPMS,
              tooltip: type.isPMS
                ? `You can't delete a ${unitDictionary}`
                : `You can't delete a ${unitDictionary} with applications`
            }
          ];

          return <ContextMenuPopover actions={actions} />;
        }
      });

    return cols;
  }, [type.isBoomScreen, isEverySyncEnabled]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const emptyStateText = getEmptyStateText({
    currentProperty,
    hasSearchParams,
    unitsDictionary: units,
    propertyDictionary: property
  });

  return (
    <Table
      rowHeight="64px"
      thClassName=""
      tdClassName="!py-2"
      emptyStateTitle={
        hasSearchParams ? "No results found" : `${Units} will appear here`
      }
      isLoading={isLoading}
      emptyStateText={emptyStateText}
      emptyStateButtonText={
        currentProperty ? `Add ${unitDictionary}` : undefined
      }
      emptyStateButtonAction={
        currentProperty
          ? () => {
              navigate(`/properties/${propertyId}/units/add`, {
                state: { property: currentProperty }
              });
            }
          : undefined
      }
      table={table}
      onRowClick={(id, rowData) => {
        navigate(`/properties/${rowData.original?.property?.id}/units/${id}`);
      }}
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      sortableKeys={sortableKeys}
      sorted={sorting}
      setSorted={setSorting}
      mobileRow={(row) => {
        return (
          <div className="">
            <div className="flex justify-between -mb-2">
              <div className="">
                {(() => {
                  const magicLinkData = row.original
                    .magic_link as MagicLink | null;
                  const active = magicLinkData?.active || false;
                  const id = magicLinkData?.id || 0;
                  return isEverySyncEnabled && row.original.pms_id ? (
                    <AllowOrDisallowApplications
                      isActive={row.original.magic_link?.active}
                      withText={false}
                    />
                  ) : (
                    <Switch
                      checked={active}
                      onChange={(active) => {
                        (async () => {
                          try {
                            const res = await addressApi.updateMagicLink(id, {
                              active,
                              listed:
                                row.original?.property?.kind === null
                                  ? undefined
                                  : row.original?.listed
                            });
                            handleAvailableForApplication(res.unit);
                          } catch (err) {
                            toast.error(getErrorMessage(err));
                          }
                        })();
                      }}
                    />
                  );
                })()}
              </div>

              <div className="translate-x-3 -translate-y-3">
                <ContextMenuPopover
                  actions={[
                    {
                      title: "Review",
                      onClick: () => {
                        navigate(
                          `/properties/${row.original?.property?.id}/units/${row.original?.id}`
                        );
                      }
                    },
                    {
                      title: `Invite to ${unitDictionary}`,
                      onClick: () => {
                        if (row.original.magic_link?.active) {
                          setSelectedUnitId(row.original.id);
                          setShowInviteApplicantsModal(true);
                        }
                      },
                      disabled: !row.original.magic_link?.active,
                      tooltip: `Can't invite to INACTIVE ${unitDictionary}`
                    },
                    {
                      title: `Delete ${unitDictionary}`,
                      onClick: () => {
                        if (row.original.can_delete && !row.original.pms_id) {
                          setShowDeleteUnitModal(true);
                          setSelectedUnitId(row.original.id);
                        }
                      },
                      color:
                        row.original.can_delete && !row.original.pms_id
                          ? "text-danger-500"
                          : "text-content-100",
                      disabled: !row.original.can_delete || row.original.pms_id,
                      tooltip: row.original.pms_id
                        ? `You can't delete a PMS ${unitDictionary}`
                        : !row.original?.can_delete
                          ? `You can't delete a ${unitDictionary} with applications`
                          : undefined
                    }
                  ]}
                />
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-base text-content-1000">
                {row.original.title}
              </p>
              <p className="font-normal text-sm text-content-500">
                {getShortAddressFromAddress(row.original.property.address)}
              </p>
            </div>

            <div className="flex flex-col gap-1.5 py-4 my-4 border-y border-alpha-40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Door className="shrink-0 stroke-content-500" size="20" />
                  <p className="font-medium text-sm text-content-500">
                    Current status
                  </p>
                </div>

                <Badge
                  size={BadgeSize.SM}
                  variant={
                    row.original.occupied
                      ? BadgeVariant.PRIMARY
                      : BadgeVariant.SUCCESS
                  }
                >
                  {unitStatus(row.original.occupied)}
                </Badge>
              </div>

              <Show if={pms.isManageAmerica}>
                <RentAmount
                  value={row.original.home_rent_amount}
                  title="Home rent"
                  isMobile
                />
                <RentAmount
                  value={row.original.site_rent_amount}
                  title="Site rent"
                  isMobile
                />
              </Show>
              <RentAmount
                value={row.original.monthly_rent_amount}
                title={pms.isManageAmerica ? "Total rent" : "Rent amount"}
                isMobile
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database01
                    className="shrink-0 stroke-content-500"
                    size="20"
                  />
                  <p className="font-medium text-sm text-content-500">Source</p>
                </div>

                <p className="font-medium text-sm text-content-500">
                  {getSourceLabel(row.original)}
                </p>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyLink(row.original.magic_link?.application_link ?? "");
              }}
            >
              Copy application link
            </Button>
          </div>
        );
      }}
    />
  );
};
